import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MIN_TERM_LENGTH,
  searchHighlightTermList,
} from "@/lib/searchHighlightTerms";
import { useHighlightOwner } from "./useHighlightOwner";
import { findSearchHits } from "../utils/documentSearchHits";
import { scrollRangeIntoView } from "../utils/scrollUtils";
import { getVisibleTextSnapshot } from "../utils/uniqueReferenceChunk";
import { liveRangeFor, staticRangesPerNode } from "../utils/visibleTextRanges";

export interface UseDocumentSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  currentIndex: number;
  totalMatches: number;
  goToNext: () => void;
  goToPrevious: () => void;
  clearSearch: () => void;
}

export interface DocumentSearch extends UseDocumentSearchReturn {
  /**
   * Hand to `DocumentRenderer`'s `onContentCommitted`. Rebuilds the hits on
   * every new parse, before paint, and keeps the reader on the hit they were
   * on.
   */
  onContentCommitted: (docRoot: HTMLElement) => void;
}

export interface UseDocumentSearchOptions {
  /**
   * Term to prefill the search with, e.g. the global search query carried in
   * the `docSearch` URL param. Applied once per distinct term, and only until
   * the user edits the field themselves.
   */
  seedQuery?: string;
  /**
   * Identifies the document on screen. The legislation viewer keeps this hook
   * mounted while the reader opens a different document, so without this a
   * second result from the same search would never scroll, the seed having
   * already been applied for that term.
   */
  documentKey?: string;
}

/**
 * One search hit: where it starts in the visible text, and the ranges that
 * paint it, one per visible text node it touches, so deleted text inside the
 * hit stays unlit.
 */
interface SearchHit {
  start: number;
  ranges: StaticRange[];
}

/**
 * How many times a single query may re-aim its "scroll to the first hit".
 *
 * The viewer keeps changing the body after the first hits are painted —
 * document links resolve, consolidation HTML arrives, page images finish
 * decoding and shift the layout — so the text first scrolled to is often
 * thrown away or moved. Each rebuild re-aims until the target is confirmed on
 * screen.
 *
 * This is a count, deliberately not a wall-clock window: heavy documents block
 * the main thread for many seconds at a time (a 300 DPI page-raster document
 * blocks for ~16s on its own), and a timer simply expires mid-freeze and fires
 * the moment the thread frees up — releasing the intent before the document has
 * settled and leaving the reader at the top with the match far below.
 */
const MAX_SCROLL_REAIMS = 5;

/**
 * Quiet period before the layout counts as settled. The watcher stops once the
 * body has stopped resizing, rather than after a fixed number of frames — a lull
 * in image decoding could satisfy a frame count while the document was still
 * growing, releasing the intent with the match about to move again.
 */
const SETTLE_QUIET_MS = 400;

function hitsInDocument(
  docRoot: HTMLElement,
  terms: readonly string[]
): SearchHit[] {
  if (!terms.some((term) => term.trim().length >= MIN_TERM_LENGTH)) {
    return [];
  }
  const snapshot = getVisibleTextSnapshot(docRoot);
  return findSearchHits(snapshot.fullText, terms)
    .map((span) => ({
      start: span.start,
      ranges: staticRangesPerNode(snapshot, span),
    }))
    .filter((hit) => hit.ranges.length > 0);
}

/** The first hit at or after `offset`, clamped to the last hit. */
function hitIndexAtOrAfter(hits: readonly SearchHit[], offset: number): number {
  let lo = 0;
  let hi = hits.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (hits[mid].start < offset) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return Math.max(0, Math.min(lo, hits.length - 1));
}

function* rangesExcept(
  hits: readonly SearchHit[],
  index: number
): Generator<StaticRange> {
  for (let i = 0; i < hits.length; i++) {
    if (i !== index) yield* hits[i].ranges;
  }
}

/** A live range over the hit's first range, to measure and scroll to. */
const liveRangeForHit = (hit: SearchHit): Range | null =>
  liveRangeFor(hit.ranges[0]);

function revealHit(hit: SearchHit): void {
  const range = liveRangeForHit(hit);
  if (!range) return;
  /**
   * Long jumps go instantly. Smooth-animating tens of thousands of pixels
   * through a page-raster document repaints every frame for no benefit, and
   * mirrors what `useScrollAndHighlight` already does for references.
   */
  const distance = Math.abs(range.getBoundingClientRect().top);
  const behavior: ScrollBehavior =
    distance > window.innerHeight ? "auto" : "smooth";
  scrollRangeIntoView(range, behavior);
}

/**
 * Searches the text of the document and paints the hits with the CSS Custom
 * Highlight API, under `search` and (the current hit) `search-current`. The
 * document DOM is never touched (ADR 0001): hits are rebuilt from the
 * visible-text snapshot whenever the renderer commits a new parse, through
 * `onContentCommitted`.
 */
export const useDocumentSearch = (
  options?: UseDocumentSearchOptions
): DocumentSearch => {
  const seedQuery = options?.seedQuery;
  const documentKey = options?.documentKey;
  const [query, setQuery] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  /**
   * Terms parsed out of a seeded query, which carries the global search's
   * advanced syntax: quoted phrases matched whole, excluded terms dropped.
   * Null once the reader edits the field, where the text is a literal search.
   */
  const [seedTerms, setSeedTerms] = useState<string[] | null>(null);
  const terms = useMemo(() => seedTerms ?? [query], [seedTerms, query]);
  const highlights = useHighlightOwner();
  /** The `.doc-root` of the newest committed parse. */
  const docRootRef = useRef<HTMLElement | null>(null);
  const hitsRef = useRef<SearchHit[]>([]);
  /** The document the hits were found in; a place in another means nothing. */
  const hitsDocumentKeyRef = useRef(documentKey);
  const currentIndexRef = useRef(0);
  // Seed terms already applied, so a re-render does not overwrite user edits
  const appliedSeedRef = useRef<string | null>(null);
  /**
   * Scroll intent for the active term: armed when a term is seeded or typed,
   * released once the target is confirmed on screen, the re-aim budget runs
   * out, or the reader takes over.
   */
  const scrollIntentRef = useRef<{
    armed: boolean;
    attempts: number;
    /** Bumped per armed term so a watcher from a previous term stops itself. */
    generation: number;
  }>({ armed: false, attempts: 0, generation: 0 });
  /** Identifies the newest settle watcher, so an older one cannot release. */
  const watcherIdRef = useRef(0);
  /** Tears down the previous settle watcher when a new one takes over. */
  const stopPreviousWatcherRef = useRef<(() => void) | null>(null);

  /** Stops content-driven rebuilds from moving the reader any further. */
  const releaseScrollIntent = useCallback(() => {
    scrollIntentRef.current.armed = false;
  }, []);

  /** Starts a fresh scroll intent with a full re-aim budget. */
  const armScrollIntent = useCallback(() => {
    scrollIntentRef.current = {
      armed: true,
      attempts: 0,
      generation: scrollIntentRef.current.generation + 1,
    };
  }, []);

  /**
   * Makes the hit at `index` current and paints it apart from the others,
   * which are left out of `search` so the two fills never stack.
   */
  const showHit = useCallback(
    (index: number) => {
      const hits = hitsRef.current;
      currentIndexRef.current = index;
      setCurrentIndex(index);
      highlights.set("search", rangesExcept(hits, index));
      highlights.set("search-current", hits[index]?.ranges ?? []);
    },
    [highlights]
  );

  /**
   * Holds the hit on screen until the document stops resizing under it.
   *
   * Hits are often painted before the body has its final height — a
   * page-raster document is a few hundred pixels tall until its images lay
   * out, then jumps to well over a hundred thousand, carrying the match tens of
   * thousands of pixels away from where it was scrolled to. Nothing re-parses
   * in that case, so the correction has to come from layout.
   *
   * Driven by ResizeObserver rather than a per-frame loop: re-aiming on every
   * frame would restart the scroll animation before it could land, so the target
   * never arrived and the watcher spun until its budget ran out.
   */
  const keepHitInView = useCallback(
    (hit: SearchHit, docRoot: HTMLElement) => {
      const generation = scrollIntentRef.current.generation;
      let quietTimer: ReturnType<typeof setTimeout> | null = null;

      // Only one watcher may be live: an older one's settle timeout would
      // otherwise release the intent this one is still using.
      stopPreviousWatcherRef.current?.();
      watcherIdRef.current += 1;
      const watcherId = watcherIdRef.current;

      /**
       * A watcher may only act while it is both the newest one and working for
       * the term that is still armed.
       */
      const isCurrent = () =>
        watcherIdRef.current === watcherId &&
        scrollIntentRef.current.generation === generation &&
        scrollIntentRef.current.armed;

      const teardown = () => {
        if (quietTimer) clearTimeout(quietTimer);
        observer.disconnect();
        for (const [type, handler] of takeoverListeners) {
          window.removeEventListener(type, handler);
        }
        if (stopPreviousWatcherRef.current === teardown) {
          stopPreviousWatcherRef.current = null;
        }
      };

      const stop = (release: boolean) => {
        const wasCurrent = isCurrent();
        teardown();
        if (release && wasCurrent) releaseScrollIntent();
      };

      /**
       * Takeover is detected from input, not from scroll offsets: the hook's own
       * scrolling emits scroll events too, and a smooth one emits a whole
       * stream of them, so attributing offsets disarmed the watcher on its own
       * first scroll. `mousedown` is what makes a scrollbar drag detectable,
       * since that fires no wheel, touch or key event.
       */
      const onTakeOver = () => stop(true);
      const takeoverListeners: Array<[string, () => void]> = [
        ["wheel", onTakeOver],
        ["touchstart", onTakeOver],
        ["keydown", onTakeOver],
        ["mousedown", onTakeOver],
      ];

      const reAim = () => {
        // Null once a re-parse threw the hit's text away.
        const range = liveRangeForHit(hit);
        if (!isCurrent() || !range) {
          stop(false);
          return;
        }
        const intent = scrollIntentRef.current;
        if (intent.attempts >= MAX_SCROLL_REAIMS) {
          stop(true);
          return;
        }
        const rect = range.getBoundingClientRect();
        const onScreen = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (onScreen) return;

        intent.attempts += 1;
        // Instant: a smooth animation would be restarted by the next resize and
        // never arrive.
        scrollRangeIntoView(range, "auto");
      };

      const armQuietTimer = () => {
        if (quietTimer) clearTimeout(quietTimer);
        quietTimer = setTimeout(() => {
          if (!isCurrent()) {
            teardown();
            return;
          }
          reAim();
          stop(true);
        }, SETTLE_QUIET_MS);
      };

      const observer = new ResizeObserver(() => {
        if (!isCurrent()) {
          teardown();
          return;
        }
        reAim();
        armQuietTimer();
      });

      observer.observe(docRoot);
      for (const [type, handler] of takeoverListeners) {
        window.addEventListener(type, handler, { passive: true });
      }
      stopPreviousWatcherRef.current = teardown;
      armQuietTimer();
    },
    [releaseScrollIntent]
  );

  /**
   * Finds the hits in `docRoot` and paints them. `keep` holds the reader's
   * place across a re-parse of the same document: the current hit becomes the
   * first one at or after where the previous current hit started. A reader
   * still on the first hit stays on the first hit, which also covers a commit
   * under a new document that still shows the old one's text (the legislation
   * viewer hands its renderer deferred HTML). A rebuild scrolls only while the
   * scroll intent for the term is still armed.
   */
  const rebuild = useCallback(
    (docRoot: HTMLElement | null, place: "keep" | "first") => {
      const hasPlace =
        place === "keep" &&
        hitsDocumentKeyRef.current === documentKey &&
        currentIndexRef.current > 0;
      const previous = hasPlace
        ? hitsRef.current[currentIndexRef.current]
        : undefined;
      const root = docRoot?.isConnected ? docRoot : null;
      const hits = root ? hitsInDocument(root, terms) : [];
      hitsRef.current = hits;
      hitsDocumentKeyRef.current = documentKey;
      showHit(previous ? hitIndexAtOrAfter(hits, previous.start) : 0);
      setTotalMatches(hits.length);

      const intent = scrollIntentRef.current;
      if (!root || hits.length === 0) return;
      if (!intent.armed || intent.attempts >= MAX_SCROLL_REAIMS) return;
      intent.attempts += 1;
      const hit = hits[currentIndexRef.current];
      revealHit(hit);
      keepHitInView(hit, root);
    },
    [terms, documentKey, showHit, keepHitInView]
  );

  const onContentCommitted = useCallback(
    (docRoot: HTMLElement) => {
      docRootRef.current = docRoot;
      rebuild(docRoot, "keep");
    },
    [rebuild]
  );

  // A new query starts at the first hit.
  const rebuildForNewTerms = useEffectEvent(() => {
    rebuild(docRootRef.current, "first");
  });
  useLayoutEffect(() => {
    rebuildForNewTerms();
  }, [terms]);

  // Prefill from the caller (global search term in the URL) once per document
  useEffect(() => {
    const seed = seedQuery?.trim();
    if (!seed || seed.length < MIN_TERM_LENGTH) return;
    // Keyed by document as well as term, so the same search term seeds again in
    // the next document the reader opens from those results.
    const seedKey = `${documentKey ?? ""}\u0000${seed}`;
    if (appliedSeedRef.current === seedKey) return;

    appliedSeedRef.current = seedKey;
    armScrollIntent();
    setSeedTerms(searchHighlightTermList(seed));
    setQuery(seed);
  }, [seedQuery, documentKey, armScrollIntent]);

  const goTo = useCallback(
    (step: 1 | -1) => {
      const hits = hitsRef.current;
      if (hits.length === 0) return;
      releaseScrollIntent();
      const index =
        (currentIndexRef.current + step + hits.length) % hits.length;
      showHit(index);
      revealHit(hits[index]);
    },
    [showHit, releaseScrollIntent]
  );

  const goToNext = useCallback(() => goTo(1), [goTo]);
  const goToPrevious = useCallback(() => goTo(-1), [goTo]);

  const updateQuery = useCallback(
    (next: string) => {
      armScrollIntent();
      setSeedTerms(null);
      setQuery(next);
    },
    [armScrollIntent]
  );

  // The new, empty terms rebuild to no hits, which clears every search range.
  const clearSearch = useCallback(() => {
    releaseScrollIntent();
    setQuery("");
    setSeedTerms(null);
  }, [releaseScrollIntent]);

  return {
    query,
    setQuery: updateQuery,
    currentIndex,
    totalMatches,
    goToNext,
    goToPrevious,
    clearSearch,
    onContentCommitted,
  };
};
